import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import Skeleton from 'antd/lib/skeleton';
import Text from 'antd/lib/typography/Text';
import { RightOutlined } from '@ant-design/icons';
import { RootState } from '@/reducers/reducers';
import {
    selectAppIssues,
    selectIsTriageExcludedForOrg,
    selectIssuesCounts,
    selectRelease,
} from '@/selectors/selectors';
import { tryFetchPaginatedEntities } from '@/actions/entities';
import { TriageViewType } from '@/actions/triage';
import { FetchablePaginatedEntityType } from '@/entityType';
import { useIsLoading } from '@/utils/hooks/helpers';
import { SubscriptionFeatureType } from '@/types';
import { antGrey, rwColors } from '@/components/styles/globalStyles';
import { useRestrictedFeatureIsDisabled } from '@/components/planRestrictions/planRestrictionHelperComponents';
import { PlanRestrictedFeatureWrapper } from '@/components/planRestrictions/PlanRestrictedFeatureWrapper';
import { createReleaseTriageRoute, TRIAGE_ISSUE_PARAM } from '@/helpers/routeHelpers';
import { CollapsibleSection } from '@/components/monitoring/CollapsibleSection';
import { LABEL_STYLE } from './FixRequestModule';
import { RoutedTriageIssueDrawer } from './TriageIssueDrawer';
import { TriageIssueRow } from './TriageIssueRow';
import { CLOSED_ISSUE_STATES } from './triageHelpers';

const PAGE_SIZE = 10;

const TRIAGE_TITLE_STYLE: React.CSSProperties = {
    color: antGrey.grey8,
    fontWeight: 600,
    fontSize: 14,
    letterSpacing: 'normal',
    textTransform: 'none',
};

interface RolloutTriageCardProps {
    orgId: string;
    appId: string;
    releaseId: string;
    releaseVersion?: string;
    rolloutStartedAt?: string;
}

export const RolloutTriageCard: React.FC<RolloutTriageCardProps> = ({
    orgId,
    appId,
    releaseId,
    releaseVersion,
    rolloutStartedAt,
}) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const openIssueId = new URLSearchParams(location.search).get(TRIAGE_ISSUE_PARAM);
    const setOpenIssueId = React.useCallback(
        (issueId: string | null) => {
            const params = new URLSearchParams(location.search);
            if (issueId) {
                params.set(TRIAGE_ISSUE_PARAM, issueId);
            } else {
                params.delete(TRIAGE_ISSUE_PARAM);
            }
            const search = params.toString();
            history.replace(search ? `${location.pathname}?${search}` : location.pathname);
        },
        [history, location.pathname, location.search],
    );
    const isTriageExcluded = useSelector(selectIsTriageExcludedForOrg);
    const isPaywalled = useRestrictedFeatureIsDisabled(SubscriptionFeatureType.Triage);
    const allIssues = useSelector((state: RootState) => selectAppIssues(state, appId));
    const { openCount } = useSelector((state: RootState) => selectIssuesCounts(state, appId));
    const isFetching = useIsLoading(FetchablePaginatedEntityType.Issues, appId);
    const routeReleaseId = useSelector((state: RootState) => selectRelease(state, releaseId)?.releaseId);

    const shouldFetch = !isTriageExcluded && !isPaywalled && !!releaseVersion;
    const fetchPage = React.useCallback(
        (pageOffset: number) => {
            dispatch(
                tryFetchPaginatedEntities(
                    FetchablePaginatedEntityType.Issues,
                    {
                        orgId,
                        appId,
                        statusGroup: 'open',
                        versions: [releaseVersion],
                        detectedAfter: rolloutStartedAt,
                    },
                    PAGE_SIZE,
                    pageOffset,
                    appId,
                ),
            );
        },
        [dispatch, orgId, appId, releaseVersion, rolloutStartedAt],
    );

    React.useEffect(() => {
        if (!shouldFetch) return;
        fetchPage(0);
    }, [shouldFetch, fetchPage]);

    const [hasLoaded, setHasLoaded] = React.useState(false);
    React.useEffect(() => {
        if (isFetching) {
            return () => setHasLoaded(true);
        }
        return undefined;
    }, [isFetching]);

    const issues = React.useMemo(
        () =>
            allIssues.filter(
                (issue) =>
                    !CLOSED_ISSUE_STATES.includes(issue.state) && (issue.versions ?? []).includes(releaseVersion ?? ''),
            ),
        [allIssues, releaseVersion],
    );

    const countLabel =
        issues.length < openCount ? `Showing ${issues.length} of ${openCount} pending` : `${openCount} pending`;

    const triagePath = createReleaseTriageRoute(orgId, appId, routeReleaseId ?? releaseVersion ?? '');

    if (isTriageExcluded) {
        return null;
    }

    if (isPaywalled) {
        return (
            <CollapsibleSection title="Triage" titleStyle={TRIAGE_TITLE_STYLE} defaultOpen>
                <div style={{ backgroundColor: antGrey.grey2, padding: 8, borderRadius: 4, width: '100%' }}>
                    <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Triage} requiresTooltip>
                        <Text style={{ color: antGrey.grey7, fontSize: 12 }}>
                            Triage issues not available on your plan.
                        </Text>
                    </PlanRestrictedFeatureWrapper>
                </div>
            </CollapsibleSection>
        );
    }

    return (
        <div>
            <CollapsibleSection
                title="Triage"
                titleStyle={TRIAGE_TITLE_STYLE}
                titleSuffix={<Text style={{ ...LABEL_STYLE, color: antGrey.grey6 }}>{countLabel}</Text>}
                defaultOpen
                extra={
                    <Link to={triagePath} style={{ color: rwColors.primary }} onClick={(e) => e.stopPropagation()}>
                        View all <RightOutlined style={{ fontSize: 10 }} />
                    </Link>
                }
            >
                {!issues.length && !hasLoaded ?
                    <Skeleton active round={false} paragraph={{ rows: 2, style: { marginBottom: 0 } }} />
                : !issues.length ?
                    <Text style={{ color: antGrey.grey7, fontSize: 13 }}>
                        No issues have been detected on this release since the rollout started.
                    </Text>
                :   null}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {issues.slice(0, PAGE_SIZE).map((issue) => (
                        <TriageIssueRow
                            key={issue.id}
                            issue={issue}
                            orgId={orgId}
                            onOpen={() => setOpenIssueId(issue.id)}
                            triagePath={triagePath}
                        />
                    ))}
                </div>
            </CollapsibleSection>
            <RoutedTriageIssueDrawer
                orgId={orgId}
                appId={appId}
                basePath={triagePath}
                issueId={openIssueId ?? undefined}
                viewType={TriageViewType.Release}
                onClose={() => setOpenIssueId(null)}
            />
        </div>
    );
};
